import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { ModifyConsolidatedForecastView } from '@futrli/views/views/forecasts/ModifyConsolidatedForecastView/ModifyConsolidatedForecastView';
import { ModifyForecastView } from '@futrli/views/views/forecasts/ModifyForecastView/ModifyForecastView';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { useRouteIds } from '@futrli/predict/hooks/useRouteIds';

interface DeleteForecastDialogProps {
  forecastId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteForecastDialog = ({ forecastId, isOpen, onClose }: DeleteForecastDialogProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId } = useRouteIds();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const ViewClass = isConsolidation ? ModifyConsolidatedForecastView : ModifyForecastView;

  const view = useApiView(
    useMemo(() => new ViewClass(workspaceId, organisationId, forecastId), [
      ViewClass,
      workspaceId,
      organisationId,
      forecastId
    ])
  );

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: view.state.isLoading || view.state.isDeleting,
    error: view.state.errorDeleting,
    onAction: () => view.deleteForecast(),
    onClose
  });

  return (
    <DeleteDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={handleAction}
      screen={screen}
      title={intl.formatMessage({ id: 'common.delete', defaultMessage: 'Delete' })}
      subtitle={view.state.forecast?.name}
      thingsToKnow={[
        intl.formatMessage({
          id: 'predict.forecasting.delete.things_to_know.irreversible',
          defaultMessage: 'Deleting a forecast is not recoverable'
        }),
        intl.formatMessage({
          id: 'predict.forecasting.delete.things_to_know.users_lose_access',
          defaultMessage: 'All users will immediately lose access to this forecast'
        })
      ]}
      deleteDescription={intl.formatMessage({
        id: 'predict.forecasting.delete_forecast_description',
        defaultMessage: 'Are you sure you want to delete this forecast?'
      })}
      loadingMessage={
        view.state.isLoading
          ? intl.formatMessage({
              id: 'common.loading_please_wait',
              defaultMessage: 'Loading, please wait...'
            })
          : intl.formatMessage({
              id: 'predict.forecasting.deleting_forecast',
              defaultMessage: 'Deleting forecast...'
            })
      }
      successMessage={intl.formatMessage({
        id: 'predict.forecasting.forecast_deleted',
        defaultMessage: 'Forecast deleted'
      })}
      errorMessage={view.state.errorDeleting?.message}
      dataTestId="delete-forecast-dialog"
    />
  );
};
